import { orderBy } from "lodash";
import { JSX, useEffect, useState } from "react";
import { AdequacyAggregateType, NetworkProviderFilterType } from "@app/exports/enumerations";
import { SharedUserModel } from "@app/exports/exportModels";
import { NetworkAssessmentAnalysisType } from "@app/projects/enumerators";
import InfoIndicator from "@app/shared/InfoIndicator";
import MultiselectDropdown from "@app/shared/checkbox-dropdown/MultiselectDropdown";
import { DropdownItem } from "@app/shared/checkbox-dropdown/types";
import ExportService from "@app/exports/exportService";

interface SharedUsersProps {
  exportType: string;
  customProjectIds: number[];
  tooltipTitle: string;
  networkAssessmentAnalysisType?: NetworkAssessmentAnalysisType;
  networkProviderFilterType?: NetworkProviderFilterType;
  aggregateType?: AdequacyAggregateType;
  updateSelectedSharedUsers: (sharedUsers: number[]) => void;
  sharedUsersFilter?: number[];
  containerClassnames?: string;
  requiredRoles?: string[];
}

const SharedUsers = (props: SharedUsersProps): JSX.Element => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [allSharedUsers, setAllSharedUsers] = useState<SharedUserModel[]>([]);
  const [sharedUsersFilter, setSharedUsersFilter] = useState<DropdownItem<number>[]>([]);

  const exportService = new ExportService();

  useEffect(() => {
    setIsLoading(true);
    fetchSharedUsers();
  }, [props.exportType, props.networkAssessmentAnalysisType, props.networkProviderFilterType, props.aggregateType, props.requiredRoles])

  useEffect(() => {
    if (allSharedUsers == undefined || sharedUsersFilter == undefined || sharedUsersFilter.length == 0)
      return;

    const users = sharedUsersFilter.filter(u => allSharedUsers.map(a => a.userId).includes(u.key));
    setSharedUsersFilter(users);
  }, [allSharedUsers])

  useEffect(() => {
    props.updateSelectedSharedUsers(sharedUsersFilter.map(u => u.key));
  }, [sharedUsersFilter])

  const fetchSharedUsers = async () => {
    try {
      const sharedUsers = await exportService.getSharedUsers(props.exportType, props.customProjectIds, props.networkAssessmentAnalysisType, props.networkProviderFilterType, props.aggregateType, props.requiredRoles);
      setAllSharedUsers(orderBy(sharedUsers, (u) => u.fullName, "asc"))
    } catch (e) {
      // if there's an error, assume no users are valid
      // this should also clear the list of selections through the useEffect above
      setAllSharedUsers([]);
    } finally {
      setIsLoading(false);
    }
  }

  const getAllSharedUsersDropdownItems = () => {
    return allSharedUsers.map(u => {
      return { key: u.userId, name: u.fullName };
    });
  }

  return (
    <>
      <MultiselectDropdown
        containerClassName={`shared-users__multi-select${props.containerClassnames ? ` ${props.containerClassnames}` : ""}`}
        label={<>
          Shared Users
          <span>&nbsp;</span>
          <InfoIndicator
            className={"informational-indicator"}
            tooltipTitle={props.tooltipTitle}
            tooltipPlacement={"right"} />
        </>}
        disabled={false}
        hideSelectAll={true}
        isLoading={isLoading}
        items={getAllSharedUsersDropdownItems()}
        noItemsDescription="Sharing is not available, no eligible users found"
        noVisibleItemsDescription="No eligible users found"
        onSelectionChanged={(selectedItems: DropdownItem<number>[]) => { setSharedUsersFilter(selectedItems) }}
        selectedItems={props.sharedUsersFilter === undefined ? sharedUsersFilter : getAllSharedUsersDropdownItems().filter(u => props.sharedUsersFilter?.includes(u.key))}
        testId="shared-users__multi-select"
        itemAlias="User(s) Selected"
      />
    </>
  );
};

export default SharedUsers;